import { useMutation, useQuery } from '@tanstack/react-query';
import {
  deleteTechnicianServiceType,
  getAssignableTechnicianServiceTypes,
  getTechnicianServiceTypes,
  postTechnicianServiceType,
  putTechnicianServiceType,
} from 'queries';
import { parseErrors, Toast } from 'helpers';
import { JsonDataItem } from 'types';

export const useTechnicianServiceTypes = (technicianId?: string) => {
  const {
    data: serviceTypes,
    isLoading: isServiceTypesLoading,
    refetch: refetchServiceTypes,
  } = useQuery(['technician-service-types', { technicianId }], getTechnicianServiceTypes, {
    enabled: !!technicianId,
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const {
    data: assignableServiceTypes,
    isLoading: isAssignableServiceTypesLoading,
    refetch: refetchAssignableServiceTypes,
  } = useQuery(['assignable-technician-service-types', { technicianId }], getAssignableTechnicianServiceTypes, {
    enabled: !!technicianId,
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: createServiceType, isLoading: isServiceTypeCreating } = useMutation(postTechnicianServiceType, {
    onSuccess: () => {
      Toast.show({ description: 'New Service Type has been added', type: Toast.Type.Success });
      refetchServiceTypes();
      refetchAssignableServiceTypes();
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: updateServiceType, isLoading: isServiceTypeUpdating } = useMutation(putTechnicianServiceType, {
    onSuccess: () => {
      Toast.show({ description: 'The Service Type has been successfully updated', type: Toast.Type.Success });
      refetchServiceTypes();
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  const { mutate: deleteServiceType, isLoading: isServiceTypeDeleting } = useMutation(deleteTechnicianServiceType, {
    onSuccess: () => {
      Toast.show({ description: 'The Service Type has been successfully deleted', type: Toast.Type.Success });
      refetchServiceTypes();
      refetchAssignableServiceTypes();
    },
    onError: (error: JsonDataItem) => {
      Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
    },
  });

  return {
    serviceTypes,
    isServiceTypesLoading,
    refetchServiceTypes,
    assignableServiceTypes,
    isAssignableServiceTypesLoading,
    createServiceType,
    isServiceTypeCreating,
    updateServiceType,
    isServiceTypeUpdating,
    deleteServiceType,
    isServiceTypeDeleting,
  };
};
